import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function parseYm(ym) {
  if (!ym) return null;
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
}
function ymKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
function formatDisplay(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return `${String(m).padStart(2, '0')}/${y}`;
}

export default function MonthPicker({ value, onChange, placeholder = 'MM/YYYY' }) {
  const committed = value ?? null;
  const [isOpen, setIsOpen] = useState(false);
  const [temp, setTemp] = useState(committed);

  // initial calendar year (fallback to current year)
  const initialYear = (committed && parseYm(committed)?.getFullYear()) || new Date().getFullYear();
  const [leftYear, setLeftYear] = useState(initialYear);

  // reset temp + calendar when closing
  useEffect(() => {
    if (!isOpen) {
      setTemp(committed);
      const baseYear = (committed && parseYm(committed)?.getFullYear()) || new Date().getFullYear();
      setLeftYear(baseYear);
    }
  }, [isOpen, committed]);

  // close on outside click
  const rootRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const display = useMemo(() => formatDisplay(committed), [committed]);

  const isMonthDisabled = (y, mIdx) => {
    const currentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const candidate = new Date(y, mIdx, 1);
    return candidate > currentMonth;
  };

  const pickMonth = (year, monthIdx) => {
    if (isMonthDisabled(year, monthIdx)) return;
    const key = ymKey(new Date(year, monthIdx, 1));
    setTemp(key);
  };

  const apply = () => {
    if (temp) {
      onChange?.(temp);
      setIsOpen(false);
    }
  };

  const reset = () => {
    setTemp(null);
    onChange?.('');
  };

  const monthBtnClass = (y, idx) => {
    const key = `${y}-${String(idx + 1).padStart(2, '0')}`;
    const isSelected = temp && key === temp;
    return [
      'mrp-month',
      isMonthDisabled(y, idx) ? 'is-disabled' : '',
      isSelected ? 'is-start is-end' : ''
    ].join(' ').trim();
  };

  return (
    <div className="mrp" ref={rootRef}>
      {/* trigger box */}
      <button
        type="button"
        className="anl-input mrp-input"
        onClick={() => setIsOpen(v => !v)}
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={faCalendarAlt} className="mrp-input-ic" />
        <span className={`mrp-input-text ${display ? '' : 'placeholder'}`}>
          {display || placeholder}
        </span>
      </button>

      {isOpen && (
        <div className="mrp-pop">
          <div className="mrp-board">
            <div className="mrp-board-head">
              <button className="mrp-icon-btn" onClick={() => setLeftYear(leftYear - 1)} aria-label="Prev year">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div className="mrp-year">{leftYear}</div>
              <button className="mrp-icon-btn" onClick={() => setLeftYear(leftYear + 1)} aria-label="Next year">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
            <div className="mrp-months">
              {MONTHS.map((label, idx) => (
                <button
                  key={label}
                  className={monthBtnClass(leftYear, idx)}
                  onClick={() => pickMonth(leftYear, idx)}
                  type="button"
                  disabled={isMonthDisabled(leftYear, idx)}
                  aria-disabled={isMonthDisabled(leftYear, idx)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mrp-right">
            <div className="mrp-actions">
              <button type="button" className="action-btn" onClick={reset}>Reset</button>
              <button
                type="button"
                className="anl-btn-primary"
                disabled={!temp}
                onClick={apply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
